import { toValue } from "vue";
import { useExpose } from "#ce/composables/useExpose";

/**
 * Exposes imperative menu visibility methods and applies their state guards.
 *
 * @param {object} options
 * @param {import("vue").Ref<boolean>} options.isActive The menu's open state.
 * @param {import("vue").MaybeRefOrGetter<boolean>} options.disabled Whether the menu is disabled.
 * @param {import("vue").MaybeRefOrGetter<boolean>} options.isPermanent Whether the menu is always visible.
 * @returns {{ show: () => void, hide: () => void }} Exposed visibility methods.
 */
export function useMenuVisibility({ isActive, disabled, isPermanent }) {
  const { exposeMethods } = useExpose();

  /** Opens the menu unless it is disabled, permanent, or already open. */
  function show() {
    if (!toValue(disabled) && !isActive.value && !toValue(isPermanent)) {
      isActive.value = true;
    }
  }

  /** Closes the menu unless it is permanent or already closed. */
  function hide() {
    if (isActive.value && !toValue(isPermanent)) {
      isActive.value = false;
    }
  }

  exposeMethods({ show, hide });

  return { show, hide };
}
